// TELEMETRIA DO RELATO: o que estava acontecendo quando quebrou.
//
// ============================================================================
// POR QUE ISTO EXISTE
//
// "Fui vender o Gui e a tela ficou branca" nao conserta nada sozinho. Falta
// saber a versao do codigo que ela estava rodando, em que aparelho, em que
// tela, com que carreira -- e, quando houve, qual foi o erro de verdade que o
// navegador registrou.
//
// Sem isso o caminho e escrever de volta perguntando, esperar a resposta, e
// tentar reproduzir no escuro. Com isso, boa parte dos relatos ja chega
// resolvida. E o mesmo desenho do `m24-bug-report38.js` do 38 Brasil.
//
// ============================================================================
// ⚠️ O QUE NAO ENTRA AQUI
//
// Nada que identifique a pessoa alem do que ela ja mandou. Sem e-mail (o
// servidor ja sabe quem e pelo token), sem nome, sem WhatsApp, sem conteudo
// de save. O que vai e a FORMA da carreira -- liga, temporada, rodada --, nao
// o conteudo dela.
//
// A politica de privacidade descreve o relato como "o texto que voce escreve,
// a tela em que estava e a versao". Se algum dia entrar mais coisa aqui, o
// texto de la muda junto. Um formulario que coleta mais do que a politica diz
// e um problema, nao uma funcionalidade.
// ============================================================================

/** Os ultimos erros que o navegador registrou. Cheio o suficiente para dar
    contexto, curto o suficiente para caber no relato. */
const ERROS = [];
const TETO_ERROS = 5;

const guardar = (texto) => {
  const limpo = String(texto || '').slice(0, 240);
  if (!limpo) return;
  // Erro repetido em laco encheria a lista com a mesma linha.
  if (ERROS[ERROS.length - 1] === limpo) return;
  ERROS.push(limpo);
  if (ERROS.length > TETO_ERROS) ERROS.shift();
};

/**
 * Comeca a escutar. Chamado uma vez, na subida do app.
 *
 * ⚠️ NAO substitui o `console.error` original -- encapsula. Trocar por uma
 * funcao propria some com o erro do console do navegador, e ai quem esta
 * depurando de verdade perde a ferramenta principal para ganhar um relato
 * melhor. Os dois convivem.
 */
export function ouvirErros() {
  if (typeof window === 'undefined') return;
  if (window.__diag82) return;          // duas chamadas nao podem dobrar o hook
  window.__diag82 = true;

  try {
    window.addEventListener('error', (e) => {
      guardar(`${e.message} (${String(e.filename || '').split('/').pop()}:${e.lineno})`);
    });
    window.addEventListener('unhandledrejection', (e) => {
      guardar(`promessa rejeitada: ${e.reason?.message || e.reason}`);
    });
    const original = console.error;
    console.error = (...args) => {
      guardar(args.map((a) => (a instanceof Error ? a.message : String(a))).join(' '));
      original.apply(console, args);
    };
  } catch { /* navegador travado: o relato vai sem os erros */ }
}

/** Quanto o save esta ocupando, em KB -- o primeiro suspeito quando "nao salva". */
function tamanhoDoSave() {
  try {
    const raw = localStorage.getItem('basquete82.carreira.v1');
    return raw ? Math.round(raw.length / 1024) : 0;
  } catch { return null; }
}

/**
 * O pacote de diagnostico que acompanha o relato.
 *
 * @param {object} o
 * @param {string} o.tela   qual tela estava aberta
 * @param {object} o.state  a carreira em andamento, se houver
 */
export function diagnostico({ tela, state } = {}) {
  const d = {};
  const tenta = (nome, fn) => { try { d[nome] = fn(); } catch { /* pula */ } };

  tenta('build', () => (typeof __BUILD__ === 'string' ? __BUILD__ : null));
  tenta('tela', () => tela || null);
  tenta('url', () => String(location.href).slice(0, 200));
  tenta('agente', () => String(navigator.userAgent || '').slice(0, 200));
  tenta('viewport', () => `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}`);
  tenta('idioma', () => navigator.language);
  tenta('fuso', () => Intl.DateTimeFormat().resolvedOptions().timeZone);
  tenta('online', () => navigator.onLine);
  tenta('saveKB', tamanhoDoSave);

  /* A FORMA da carreira, nao o conteudo. Isso e o que responde "da para
     reproduzir?" -- quase todo defeito de motor depende da fase e da rodada. */
  if (state) {
    tenta('carreira', () => ({
      liga: state.leagueId,
      clube: state.teams?.find((t) => t.id === state.clubId)?.name,
      temporada: state.season,
      rodada: state.round,
      fase: state.phase,
      historico: state.history?.length || 0,
      elenco: state.teams?.find((t) => t.id === state.clubId)?.roster?.length,
      desempregado: !!state.unemployed,
      // Marca se e o desafio da semana: as regras sao outras, e o relato muda.
      desafio: state.desafio?.semana || null,
    }));
  }

  if (ERROS.length) d.erros = ERROS.slice(-3);
  return d;
}

/**
 * Prepara a imagem anexada: reduz e comprime ANTES de sair do aparelho.
 *
 * ⚠️ Um print de celular moderno tem 3 a 8 MB. Mandar isso cru significa
 * esperar meio minuto no 4G, estourar o teto de corpo do servidor e guardar
 * oito megas para ver um botao cortado. Reduzido para 1280px de lado e JPEG a
 * 70%, o mesmo print fica em 100-300 KB e continua legivel -- que e tudo que
 * um print de defeito precisa ser.
 */
export function prepararImagem(arquivo, { maxLado = 1280, qualidade = 0.7 } = {}) {
  return new Promise((resolve, reject) => {
    if (!arquivo) return reject(new Error('Nenhum arquivo.'));
    if (!/^image\//.test(arquivo.type)) return reject(new Error('Isso nao parece uma imagem.'));

    const leitor = new FileReader();
    leitor.onerror = () => reject(new Error('Nao consegui ler o arquivo.'));
    leitor.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Nao consegui abrir essa imagem.'));
      img.onload = () => {
        try {
          const escala = Math.min(1, maxLado / Math.max(img.width, img.height));
          const l = Math.round(img.width * escala);
          const a = Math.round(img.height * escala);
          const cv = document.createElement('canvas');
          cv.width = l; cv.height = a;
          const ctx = cv.getContext('2d');
          /* Fundo branco antes de desenhar: PNG com transparencia vira preto
             no JPEG, e um print com fundo preto nao se le. */
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, l, a);
          ctx.drawImage(img, 0, 0, l, a);
          resolve({
            dataUri: cv.toDataURL('image/jpeg', qualidade),
            largura: l,
            altura: a,
          });
        } catch (e) {
          reject(new Error('Nao consegui preparar essa imagem.'));
        }
      };
      img.src = leitor.result;
    };
    leitor.readAsDataURL(arquivo);
  });
}
